import configLib from '../../../configLib';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { getNewsByAccessId } from '../../store/news';
import { isEmpty } from '../../util/typeGuards';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { GatewayRequestType, GatewayResponseType } from '../web-socket/types';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { DEFAULT_NEWS_PAGE_NUMBER, DEFAULT_NEWS_PAGE_SIZE } from './constants';
import { NewsSubscriptionRequestType } from './types';

const LOG_PREFIX = '[NEWS-SUBS]';
const logger = LoggerService.getLogger();

interface PendingUnsubscription {
  timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * News Subscription Service
 */
export class NewsSubscriptionService extends BaseSubscriptionService<NewsSubscriptionRequestType> {
  private static instance: NewsSubscriptionService | undefined;

  private pendingUnsubscriptions: PendingUnsubscription[] = [];

  // Make the constructor private, to have a single instance
   
  private constructor() {
    super();
  }

  static getInstance(): NewsSubscriptionService {
    this.instance ??= new NewsSubscriptionService();
    return this.instance;
  }

  private cancelPendingUnsubscriptions(): void {
    this.pendingUnsubscriptions.forEach(pending => {
      clearTimeout(pending.timeoutId);
    });
    this.pendingUnsubscriptions = [];
  }

  private scheduleDelayedUnsubscription(): void {
    const timeoutId = setTimeout(() => {
      this.executeUnsubscription();
      // Remove from pending list
      this.pendingUnsubscriptions = this.pendingUnsubscriptions.filter(item => item.timeoutId !== timeoutId);
    }, PredefinedMilisecondsTypeEnum.halfSecond);

    this.pendingUnsubscriptions.push({ timeoutId });
  }

  private executeUnsubscription(): void {
    this.subscripitonRequests = []; // Remove news request from the list
  }

  public subscribe(): void {
    this.cancelPendingUnsubscriptions();

    const subscripitonRequest = this.subscripitonRequests[0];
    if (subscripitonRequest) { // This news request already in the subscription request list!
      return;
    }

    const request = {
      pageSize: DEFAULT_NEWS_PAGE_SIZE,
      pageNumber: DEFAULT_NEWS_PAGE_NUMBER,
      ...this.getDefaultRequestParams(),
    };

    this.subscripitonRequests.push(request);
    this.gatewayService.sendRequest(request);
  }

  public subscribeList = (item: string[], args: any): void => {
    // There is no such need right now
    throw new Error('Not implemented yet!');
  };

  public unsubscribe(): void {
    const subscripitonRequest = this.subscripitonRequests[0];
    if (!subscripitonRequest) { // This now news request in the subscription request list!
      return;
    }

    this.scheduleDelayedUnsubscription();
  }

  public unsubscribeList = (item: string[], args: any): void => {
    // There is no such need right now
    throw new Error('Not implemented yet!');
  };

  public handleResponseEvent(event: WebSocketEventData<any>): void {
    const data = event.data ?? {};

    const {
      accessId,
      traceIdentifier,
    } = data || {};

    const traceIdentifierRequest = (this.gatewayService.getRequestedObjectList().find(
      item => item.messageType === this.getSubscriptionRequestType()
        && item.traceIdentifier === traceIdentifier,
    ) ?? {}) as NewsSubscriptionRequestType;

    const symbol = ({ ...traceIdentifierRequest } as any).symbols;
    if (!isEmpty(symbol) || isEmpty(traceIdentifierRequest)) {
      logger.warn(`${LOG_PREFIX} Latest news response contains symbol data - this should be handled by SymbolNewsSubscriptionService`);
      return;
    }

    configLib.store.dispatch(getNewsByAccessId({
      accessId,
      traceIdentifier,
    }));
  }

  protected shouldHandleResponse(event: WebSocketEventData<any>): boolean {
    return event.data?.messageType === this.getResponseType();
  }

  protected getResponseType(): GatewayResponseType {
    return GatewayResponseType.DataQueryResponse;
  }

  protected getSubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.GetLatestNewsArticles;
  }

  protected getUnsubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.GetLatestNewsArticles;
  }
}
